import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { Film } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { InlineField } from "@/components/ui/inline-edit"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { inlinePatch } from "@/lib/inline-save"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { PhotoSelectField, type PhotoSelection } from "@/components/photo-library/PhotoSelectField"
import type { LibraryPhoto } from "@/components/pages-cms/types"
import type { PageProps } from "@/types/inertia"

type Video = {
  id: number
  title: string
  embed_url: string | null
  embed_src: string | null
  thumbnail_url: string | null
  file_url: string | null
  /** The chosen poster — the frame shown before an uploaded video plays. */
  poster_url: string | null
  media_section_id: number | null
  section_name: string | null
}

// value is a MediaSection id — the same folders the photo library uses
type SectionOption = { value: string; name: string; kind: string }

type Props = PageProps & {
  videos: Video[]
  sections: SectionOption[]
  photo_library: LibraryPhoto[]
  filters: { section?: string }
}

function VideoPreview({ video }: { video: Video }) {
  // The poster first: it is what the public page shows before play, so it is
  // what this screen should show too.
  const still = video.poster_url ?? video.thumbnail_url
  if (still) {
    return <img src={still} alt="" className="aspect-video w-full object-cover" />
  }
  if (video.file_url) {
    // #t=0.1 so a video with no thumbnail yet still paints a frame here
    // rather than the blank box that sent us looking for this in the first
    // place.
    return (
      <video
        src={`${video.file_url}#t=0.1`}
        preload="metadata"
        muted
        className="aspect-video w-full object-cover"
      />
    )
  }
  return (
    <div className="flex aspect-video items-center justify-center bg-surface text-ink-muted">
      <Film className="h-8 w-8" />
    </div>
  )
}

function EditVideoDialog({
  video,
  sections,
  library,
  onClose,
}: {
  video: Video
  sections: SectionOption[]
  library: LibraryPhoto[]
  onClose: () => void
}) {
  const fields = useInlineRecord({
    record: {
      title: video.title,
      embed_url: video.embed_url ?? "",
      media_section_id: video.media_section_id ? String(video.media_section_id) : "",
    },
    url: `/admin/videos/${video.id}`,
    param: "video",
  })
  const v = fields.values

  const titleState = fields.state("title")
  const embedState = fields.state("embed_url")
  const sectionState = fields.state("media_section_id")

  // The thumbnail is a file rather than a value: it goes up as soon as it is
  // chosen, since you can see from the preview whether it is the right one.
  const [poster, setPoster] = React.useState<PhotoSelection>({ kind: "unchanged" })

  function choosePoster(selection: PhotoSelection) {
    setPoster(selection)
    if (selection.kind === "library") {
      inlinePatch(`/admin/videos/${video.id}`, { video: { photo_id: selection.id } })
    } else if (selection.kind === "upload") {
      inlinePatch(
        `/admin/videos/${video.id}`,
        { video: { photo: selection.file } },
        { forceFormData: true },
      )
    }
  }

  function remove() {
    router.delete(`/admin/videos/${video.id}`, { onSuccess: onClose })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit video</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <VideoPreview video={video} />

          <InlineField label="Title" htmlFor="video-title" state={titleState}>
            <Input
              id="video-title"
              value={v.title}
              onChange={(e) => fields.set("title", e.target.value)}
              onKeyDown={titleState.keys}
            />
          </InlineField>
          {!video.file_url && (
            <InlineField label="YouTube or Vimeo link" htmlFor="video-embed-url" state={embedState}>
              <Input
                id="video-embed-url"
                value={v.embed_url}
                onChange={(e) => fields.set("embed_url", e.target.value)}
                onKeyDown={embedState.keys}
                placeholder="https://www.youtube.com/watch?v=…"
              />
            </InlineField>
          )}
          {video.file_url && (
            <div className="space-y-1.5">
              <label htmlFor="video-poster">Thumbnail</label>
              <PhotoSelectField
                library={library}
                currentUrl={video.poster_url}
                value={poster}
                onChange={choosePoster}
                shape="landscape"
                inputId="video-poster"
              />
              <p className="text-xs text-ink-muted">
                The still shown before the video plays, here and on the location page. Without one the player
                shows a black box until someone presses play. Choosing one saves it straight away.
              </p>
            </div>
          )}
          <InlineField label="Section" htmlFor="video-section" state={sectionState}>
            <Select
              id="video-section"
              value={v.media_section_id}
              onChange={(e) => fields.set("media_section_id", e.target.value)}
            >
              <option value="">No section</option>
              {sections.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.name}
                </option>
              ))}
            </Select>
          </InlineField>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            className="text-danger-display hover:border-danger hover:bg-danger-faded"
            onClick={remove}
          >
            Remove video
          </Button>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function VideosIndex() {
  const { props } = usePage<Props>()
  const { videos, sections, photo_library, filters } = props
  const flash = props.flash

  const [section, setSection] = React.useState(filters.section ?? "")
  // The row from the current props, not a snapshot taken when the dialog
  // opened: every inline save refreshes the list, and the dialog should show
  // what the server now holds.
  const [editingId, setEditingId] = React.useState<number | null>(null)
  const editing = videos.find((v) => v.id === editingId) ?? null

  const [addTitle, setAddTitle] = React.useState("")
  const [addEmbedUrl, setAddEmbedUrl] = React.useState("")
  const [addFile, setAddFile] = React.useState<File | null>(null)
  const [addSection, setAddSection] = React.useState("")
  const [adding, setAdding] = React.useState(false)

  function applyFilter(value: string) {
    setSection(value)
    router.get(
      "/admin/videos",
      { section: value || undefined },
      { preserveState: true, replace: true },
    )
  }

  function add() {
    if (!addTitle.trim() || (!addEmbedUrl.trim() && !addFile)) return
    const fd = new FormData()
    fd.append("video[title]", addTitle)
    if (addEmbedUrl.trim()) fd.append("video[embed_url]", addEmbedUrl.trim())
    if (addFile) fd.append("video[file]", addFile)
    if (addSection) fd.append("video[media_section_id]", addSection)

    setAdding(true)
    router.post("/admin/videos", fd, {
      onFinish: () => setAdding(false),
      onSuccess: () => {
        setAddTitle("")
        setAddEmbedUrl("")
        setAddFile(null)
        setAddSection("")
      },
    })
  }

  return (
    <AdminShell>
      <Head title="Videos">
        <meta name="description" content="Central video library for Sanctuary Therapy Centre." />
        <meta property="og:title" content="Videos" />
        <meta property="og:description" content="Central video library for Sanctuary Therapy Centre." />
      </Head>

      <PageHeader
        title="Videos"
        description="Video tours and clips. A video assigned to a location's section is shown on that location's public page."
        actions={
          <Button variant="secondary" asChild>
            <Link href="/admin/photos/sections">Manage sections</Link>
          </Button>
        }
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {flash?.alert && <p className="mt-4 text-sm text-danger-display">{flash.alert}</p>}

      {/* Add */}
      <div className="mt-6 rounded-lg border border-dashed border-hairline p-4">
        <p className="mb-3 text-sm font-medium text-ink-display">Add a video</p>
        <p className="mb-3 text-sm text-ink-muted">
          Paste a YouTube or Vimeo link (recommended), or upload a video file directly.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            placeholder="Title (e.g. Prestwich video tour)"
            value={addTitle}
            onChange={(e) => setAddTitle(e.target.value)}
          />
          <Select value={addSection} onChange={(e) => setAddSection(e.target.value)}>
            <option value="">No section</option>
            {sections.map((s) => (
              <option key={s.value} value={s.value}>
                {s.name}
              </option>
            ))}
          </Select>
          <Input
            placeholder="YouTube or Vimeo link"
            value={addEmbedUrl}
            onChange={(e) => setAddEmbedUrl(e.target.value)}
          />
          <Input
            type="file"
            accept="video/*"
            onChange={(e) => setAddFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <Button
          type="button"
          className="mt-3"
          disabled={adding || !addTitle.trim() || (!addEmbedUrl.trim() && !addFile)}
          onClick={add}
        >
          Add video
        </Button>
      </div>

      {/* Filter */}
      <div className="mt-6 flex flex-wrap gap-3">
        <Select value={section} onChange={(e) => applyFilter(e.target.value)} className="w-48">
          <option value="">All sections</option>
          <option value="none">Unfiled</option>
          {sections.map((s) => (
            <option key={s.value} value={s.value}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      {/* Grid */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {videos.length === 0 && (
          <p className="col-span-full py-8 text-center text-ink-muted">No videos yet.</p>
        )}
        {videos.map((video) => (
          <button
            key={video.id}
            type="button"
            onClick={() => setEditingId(video.id)}
            className="cursor-pointer overflow-hidden rounded-lg border border-hairline text-left transition-colors hover:border-accent"
          >
            <VideoPreview video={video} />
            <div className="space-y-1 p-2.5">
              <p className="truncate text-xs font-medium text-ink-display">{video.title}</p>
              {video.section_name && <p className="text-[11px] text-ink-muted">{video.section_name}</p>}
            </div>
          </button>
        ))}
      </div>

      {editing && (
        <EditVideoDialog
          video={editing}
          sections={sections}
          library={photo_library}
          onClose={() => setEditingId(null)}
        />
      )}
    </AdminShell>
  )
}
